// WCAG colour helpers for airline brand colours.
//
// The staff sign-in pages use the airline's exact color1 for surfaces (card header, buttons);
// readableTextOn picks white or near-black text for it. Small text links on white need 4.5:1, so
// setCompanyStyle derives --brand-link with ensureAaOnWhite(color1, { min: 4.5 }) — the airline's
// colour, darkened only when it is too light to read as text.

export const VAMOS_PRIMARY = '#075FF7'
export const DARK_TEXT = '#111317'

const HEX_RE = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i

/** '#abc' | 'abc' | '#aabbcc' → '#aabbcc' (lowercase), or null when not a hex colour. */
export function normaliseHex(value) {
  if (typeof value !== 'string') return null
  const match = value.trim().match(HEX_RE)
  if (!match) return null
  let hex = match[1].toLowerCase()
  if (hex.length === 3) hex = hex.replace(/./g, (c) => c + c)
  return `#${hex}`
}

export function parseHex(value) {
  const hex = normaliseHex(value)
  if (!hex) return null
  return {
    r: parseInt(hex.slice(1, 3), 16),
    g: parseInt(hex.slice(3, 5), 16),
    b: parseInt(hex.slice(5, 7), 16),
  }
}

export function toHex({ r, g, b }) {
  const part = (n) =>
    Math.max(0, Math.min(255, Math.round(n)))
      .toString(16)
      .padStart(2, '0')
  return `#${part(r)}${part(g)}${part(b)}`
}

/** 'r,g,b' for CSS rgba(var(--x-rgb), a) usage. */
export function toRgbTriplet(value) {
  const rgb = parseHex(value)
  return rgb ? `${rgb.r},${rgb.g},${rgb.b}` : null
}

// WCAG 2.x relative luminance.
export function relativeLuminance(value) {
  const rgb = parseHex(value)
  if (!rgb) return null
  const channel = (c) => {
    const s = c / 255
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b)
}

export function contrastRatio(a, b) {
  const la = relativeLuminance(a)
  const lb = relativeLuminance(b)
  if (la === null || lb === null) return null
  const [hi, lo] = la > lb ? [la, lb] : [lb, la]
  return (hi + 0.05) / (lo + 0.05)
}

/**
 * White text when it reaches `min`:1 on `bg` (4.5 = body text; 3 = bold UI text such as button
 * labels and card headers), otherwise near-black.
 */
export function readableTextOn(bg, { min = 4.5 } = {}) {
  const ratio = contrastRatio('#ffffff', bg)
  return ratio !== null && ratio >= min ? '#ffffff' : DARK_TEXT
}

/**
 * Darkens `value` until it reaches `min`:1 against white (3:1 = WCAG non-text / UI component
 * threshold). Already-dark colours come back unchanged (normalised). Invalid input falls back
 * to the Vamos primary.
 */
export function ensureAaOnWhite(value, { min = 3 } = {}) {
  const rgb = parseHex(value)
  if (!rgb) return VAMOS_PRIMARY
  let current = { ...rgb }
  for (let i = 0; i < 40; i += 1) {
    const hex = toHex(current)
    if (contrastRatio(hex, '#ffffff') >= min) return hex
    current = { r: current.r * 0.92, g: current.g * 0.92, b: current.b * 0.92 }
  }
  return toHex(current)
}

/** `value` mixed towards black (amount < 0) or white (amount > 0), amount in [-1, 1]. */
export function mix(value, amount) {
  const rgb = parseHex(value)
  if (!rgb) return value
  const target = amount < 0 ? 0 : 255
  const t = Math.abs(amount)
  return toHex({
    r: rgb.r + (target - rgb.r) * t,
    g: rgb.g + (target - rgb.g) * t,
    b: rgb.b + (target - rgb.b) * t,
  })
}
